import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEOHead } from '../../hooks/useSEOHead';
import { injectJsonLd, removeJsonLd, buildWebPageSchema, buildBreadcrumbSchema } from '../../lib/schemaBuilder';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import Breadcrumb from '../../components/feature/Breadcrumb';

const BASE = 'https://rockhoundauto.com';

const COMPARISON = [
  { label: 'Doc Fee', us: '$250 flat', them: '$500–$1,200' },
  { label: 'Price Transparency', us: 'Full — before you ask', them: 'Hidden until signing' },
  { label: 'Vehicle Inspection', us: 'Every car, every time', them: 'Varies — often skipped' },
  { label: 'CarFax Report', us: 'Free, no strings', them: 'Often charged extra' },
  { label: 'Sales Pressure', us: 'Zero. Guaranteed.', them: 'Standard practice' },
  { label: 'Delivery', us: 'Free within 50 mi', them: 'Rarely offered' },
  { label: 'Charity Donation', us: '$100 to dog rescue', them: 'None' },
];

const BENEFITS = [
  {
    icon: 'ri-price-tag-3-line',
    title: '$250 Flat Doc Fee',
    description: 'Oregon dealers average $500–$1,200 in doc fees. We charge $250. That\'s it. The savings alone could cover a tank of gas or a nice dinner.',
    link: null,
    linkLabel: null,
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Inspected Before You See It',
    description: 'Every vehicle passes a full safety check — brakes, lights, steering, suspension. If it doesn\'t pass, it doesn\'t go on the lot.',
    link: null,
    linkLabel: null,
  },
  {
    icon: 'ri-hand-heart-line',
    title: 'Financing for Real Life',
    description: 'Bad credit, no credit, rebuilding — we work with lenders who deal in reality. You deserve reliable transportation and car financing for all credit types.',
    link: '/financing',
    linkLabel: 'Explore Financing',
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'Free CarFax — No Strings',
    description: 'Full accident history, service records, ownership count — handed to you before you even ask. Knowledge is yours.',
    link: null,
    linkLabel: null,
  },
  {
    icon: 'ri-truck-line',
    title: 'We Come to You',
    description: 'Free delivery within 50 miles. Browse online, get approved, and have your car show up at your door. We offer free delivery throughout the Oregon Coast.',
    link: '/oregon-coast-delivery',
    linkLabel: 'See Delivery Areas',
  },
  {
    icon: 'ri-heart-3-line',
    title: 'Your Purchase Saves a Dog',
    description: '$100 from every sale goes directly to Oregon Dog Rescue — funding real rescues, vet care, and adoptions.',
    link: '/community-impact',
    linkLabel: 'See Our Impact',
  },
];

function HeroSection() {
  return (
    <section className="relative bg-[#1A2332] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/6674c65de4f7ec94ea4f788ccc87711e.jpg"
          alt=""
          className="w-full h-full object-cover object-top opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A2332]/90 via-[#1A2332]/70 to-[#1A2332]/50" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <span className="inline-block px-3 py-1 bg-[#E8B84B]/20 border border-[#E8B84B]/40 text-[#E8B84B] text-xs font-bold uppercase tracking-widest rounded-full mb-5">
          No Marketing Fluff
        </span>
        <h1 className="text-4xl md:text-6xl font-bold text-white mb-5 leading-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>
          The Rockhound Difference
        </h1>
        <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-8">
          Real, measurable differences that save you money, time, and stress — compared side-by-side against typical Oregon dealerships.
        </p>
        <Link
          to="/inventory"
          className="inline-flex items-center gap-2 px-7 py-3 bg-[#E8B84B] text-[#1A2332] rounded-xl font-bold text-sm hover:bg-[#d4a43a] transition-colors whitespace-nowrap cursor-pointer"
        >
          Browse Inventory
          <i className="ri-arrow-right-line" aria-hidden="true"></i>
        </Link>
      </div>
    </section>
  );
}

function ComparisonSection() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section ref={ref} className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className="text-center mb-12 transition-all duration-700"
          style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(20px)' }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-[#1A2332] mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
            Us vs. Typical Dealers
          </h2>
          <p className="text-[#4A5568] max-w-xl mx-auto text-sm">
            We publish our fees upfront. Always ask for the final out-the-door number before you sit down anywhere.
          </p>
        </div>

        <div
          className="grid lg:grid-cols-2 gap-12 items-start transition-all duration-700 delay-100"
          style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(20px)' }}
        >
          {/* Comparison Table */}
          <div>
            <div className="bg-[#F7F5F0] rounded-2xl border border-[#E8E2D6] overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[320px]" aria-label="Rockhound Auto vs typical dealer comparison">
                  <thead>
                    <tr className="bg-[#1A2332] text-white text-xs font-bold uppercase tracking-wider">
                      <th scope="col" className="px-4 py-3 text-left text-white">Feature</th>
                      <th scope="col" className="px-4 py-3 text-center text-[#E8B84B]">Rockhound</th>
                      <th scope="col" className="px-4 py-3 text-center text-white/80">Typical Dealer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {COMPARISON.map((row, i) => (
                      <tr
                        key={i}
                        className={`border-b border-[#E8E2D6] last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-[#F7F5F0]'}`}
                      >
                        <th scope="row" className="px-4 py-3 text-xs font-semibold text-[#1A2332] text-left">{row.label}</th>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center gap-1 text-xs font-bold text-[#2A7A5A]">
                            <i className="ri-check-line text-sm" aria-hidden="true"></i>
                            {row.us}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-xs text-[#4A5568]">{row.them}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <Link
                to="/compare"
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#1A2332] text-[#E8B84B] rounded-xl font-bold text-sm hover:bg-[#2C3E55] transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-bar-chart-grouped-line" aria-hidden="true"></i>
                Full Comparison
              </Link>
              <Link
                to="/how-it-works"
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 border-2 border-[#E8E2D6] text-[#1A2332] rounded-xl font-bold text-sm hover:border-[#92650a] hover:text-[#92650a] transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-question-line" aria-hidden="true"></i>
                How It Works
              </Link>
            </div>
          </div>

          {/* Expert Callout */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-l-4 border-[#E8B84B] rounded-2xl p-7 shadow-sm">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-12 h-12 flex items-center justify-center bg-[#E8B84B] rounded-full flex-shrink-0">
                <i className="ri-lightbulb-line text-xl text-[#1A2332]" aria-hidden="true"></i>
              </div>
              <div>
                <h3 className="text-base font-bold text-[#1A2332] mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  Why Dealers Get Away With It
                </h3>
                <p className="text-sm text-[#374151] leading-relaxed">
                  Franchise dealers add $1,500–$3,000 in fees that independent brokers don't charge — doc fees, dealer prep, market adjustments, mandatory add-ons. They bury these in the fine print and reveal them at signing when you're emotionally committed.
                </p>
              </div>
            </div>
            <p className="text-sm text-[#374151] leading-relaxed border-t border-[#E8B84B]/30 pt-4">
              We publish our fees upfront: <strong className="text-[#1A2332]">$995 flat broker fee. That's it.</strong> When you compare total out-the-door prices — not just sticker prices — the savings become obvious. Always ask for the final number before you sit down.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function BenefitsSection() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section ref={ref} className="py-16 bg-[#F7F5F0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className="text-center mb-12 transition-all duration-700"
          style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(20px)' }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-[#1A2332] mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
            What You Actually Get
          </h2>
          <p className="text-[#4A5568] max-w-xl mx-auto text-sm">
            Six concrete benefits every Rockhound customer receives — no asterisks, no fine print.
          </p>
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-700 delay-100"
          style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(20px)' }}
        >
          {BENEFITS.map((b, i) => (
            <div
              key={i}
              className="bg-white rounded-xl border border-[#E8E2D6] p-6 hover:border-[#92650a] hover:shadow-md transition-all duration-200 flex flex-col"
            >
              <div className="w-11 h-11 flex items-center justify-center bg-[#E8B84B]/10 rounded-lg border border-[#E8B84B]/30 mb-4 flex-shrink-0">
                <i className={`${b.icon} text-xl text-[#7a5208]`} aria-hidden="true"></i>
              </div>
              <h3 className="text-base font-bold text-[#1A2332] mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
                {b.title}
              </h3>
              <p className="text-sm text-[#374151] leading-relaxed flex-1">
                {b.description}
              </p>
              {b.link && b.linkLabel && (
                <Link
                  to={b.link}
                  className="inline-flex items-center gap-1 mt-4 text-xs font-semibold text-[#7a5208] hover:text-[#1A2332] transition-colors whitespace-nowrap cursor-pointer"
                >
                  {b.linkLabel} <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaSection() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });

  return (
    <section ref={ref} className="py-16 bg-[#1A2332]">
      <div
        className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-700"
        style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(20px)' }}
      >
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>
          Ready to Experience the Difference?
        </h2>
        <p className="text-gray-300 mb-8 text-sm">
          Browse our current inventory or reach out — no pressure, no games, just honest car buying on the Oregon Coast.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/inventory"
            className="inline-flex items-center justify-center gap-2 px-7 py-3 bg-[#E8B84B] text-[#1A2332] rounded-xl font-bold text-sm hover:bg-[#d4a43a] transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-car-line" aria-hidden="true"></i>
            Browse Inventory
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 px-7 py-3 border-2 border-white/30 text-white rounded-xl font-bold text-sm hover:border-[#E8B84B] hover:text-[#E8B84B] transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line" aria-hidden="true"></i>
            Get in Touch
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function RockhoundDifferencePage() {
  useSEOHead({
    title: 'The Rockhound Difference | Transparent Pricing, No Dealer Games | Rockhound Auto',
    description: 'See exactly how Rockhound Auto differs from typical Oregon dealerships — $250 flat doc fee, free CarFax, zero sales pressure, free delivery within 50 miles.',
    keywords: 'rockhound difference, transparent car buying, no dealer fees Oregon, honest used car dealer Oregon coast',
    canonicalUrl: `${BASE}/rockhound-difference`,
  });

  useEffect(() => {
    const webpageSchema = buildWebPageSchema({
      name: 'The Rockhound Difference | Transparent Pricing, No Dealer Games',
      description: 'See exactly how Rockhound Auto differs from typical Oregon dealerships — $250 flat doc fee, free CarFax, zero sales pressure, free delivery within 50 miles.',
      url: `${BASE}/rockhound-difference`,
      breadcrumbs: [
        { name: 'Home', item: BASE },
        { name: 'The Rockhound Difference', item: `${BASE}/rockhound-difference` },
      ],
    });

    const breadcrumbSchema = buildBreadcrumbSchema([
      { name: 'Home', item: BASE },
      { name: 'The Rockhound Difference', item: `${BASE}/rockhound-difference` },
    ]);

    injectJsonLd('schema-rhdiff-webpage', webpageSchema);
    injectJsonLd('schema-rhdiff-breadcrumb', breadcrumbSchema);

    return () => {
      removeJsonLd('schema-rhdiff-webpage');
      removeJsonLd('schema-rhdiff-breadcrumb');
    };
  }, []);

  return (
    <>
      <Breadcrumb items={[{ label: 'The Rockhound Difference' }]} />
      <HeroSection />
      <ComparisonSection />
      <BenefitsSection />
      <CtaSection />
    </>
  );
}
